import React from 'react';
import styled from '@emotion/styled';
import { duration, easing, palette, radius, spacing, typography } from '../../style-utils/theme';
import { scaleIn } from '../../style-utils/motion';

/**
 * A COUNT ON A CONTROL — "3 expected today" on a menu card.
 *
 * Modelled on the notification bell's Badge, with one deliberate difference:
 * brand or neutral, NEVER alert. The bell's coral says "something needs you";
 * a count of expected visitors is Tuesday, and painting it coral invents an
 * emergency the data does not carry.
 *
 * Absent at zero rather than drawn as "0" (the bell's rule: an empty count
 * should look calm), and capped at "99+" so it never grows wider than a word.
 *
 * aria-hidden. The number belongs in the OWNING control's accessible name —
 * "Expected, 3 today" — where it is read with the thing it counts. A badge
 * that announced itself would be read as a stray "3" after the label.
 *
 * Inline, not absolutely positioned: the owner decides where it sits (the
 * StyledButton trailing slot, a tab label).
 *
 * Both fills carry white text above 4.5:1: lagoon 5.07:1, slate 5.87:1.
 */

export type CountBadgeTone = 'brand' | 'neutral';

type ToneProps = { $tone: CountBadgeTone };

const badgeBg = (p: ToneProps) => {
  return p.$tone === 'neutral' ? palette.slate : palette.lagoon;
};

const Badge = styled.span<ToneProps>`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: ${spacing.md};
  height: ${spacing.md};
  padding: 0 ${spacing.xs};
  border-radius: ${radius.pill};
  background: ${badgeBg};
  color: ${palette.white};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  line-height: 1;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  animation: ${scaleIn} ${duration.base} ${easing.spring};
`;

export interface CountBadgeProps {
  count: number;
  tone?: CountBadgeTone;
  className?: string;
}

/** What the badge shows for a count, or null when it should not be drawn. */
export const countBadgeText = (count: number): string | null => {
  if (!Number.isFinite(count) || count < 1) return null;

  return count > 99 ? '99+' : String(Math.floor(count));
};

export const CountBadge: React.FC<CountBadgeProps> = ({
  count,
  tone = 'brand',
  className,
}) => {
  const text = countBadgeText(count);

  if (text === null) return null;

  return (
    <Badge $tone={tone} data-tone={tone} aria-hidden="true" className={className}>
      {text}
    </Badge>
  );
};
